import { useEffect, useState, FC, FormEvent } from 'react'
import { useSelector } from 'react-redux'
import { map } from 'lodash-es'
import useDynamicAuthorization from '@hooks/useDynamicAuthorization'
import { fetchGroups, createPermission } from '../../actions'
import { useAppDispatch } from '@hooks/useAppDispatch'
import PermissionRoles from '@constants/permission_roles'
import Status from '@constants/request_statuses'
import BaseErrorMessages from '@components/forms/BaseErrorMessages'
import HorizontalSpinner from '@components/HorizontalSpinner'
import Form from '@components/forms/form'
import FormGroup from '@components/forms/form_group'
import Label from '@components/forms/label'
import Select from '@components/forms/select'
import SpinnerButton from '@components/forms/spinner_button'
import UpgradePrompt from '@components/UpgradePrompt'
import { getRoleSelectOptions } from '../../utils'
import { selectGroups, selectFetchGroupsStatus, selectFetchGroupsError } from '../../reducers'
import {
  selectCreatePermissionStatus,
  selectCreatePermissionError,
  selectCreatePermissionMessage,
} from '../../reducers'
import { Group, GroupRecord } from '../../types'

type Props = {
  resourceId: string | number
  resourceType: string
}

type FormState = {
  groupId?: string | number
  role: 'Viewer' | 'Editor' | 'Admin'
}

type ChangeObject = {
  name: string
  value: string
}

type ValidationErrors = {
  groupId?: string[]
  role?: string[]
}

const GroupPermissionCreator: FC<Props> = props => {
  const { resourceId, resourceType } = props

  const dispatch = useAppDispatch()

  // dynamic abilities
  const {
    status: abilitiesStatus,
    refreshAbilities,
    can,
  } = useDynamicAuthorization({
    resourceId,
    resourceType,
    abilities: [
      'groups_feature_ability', // This is to check is being checked
      'can_create_groups', // this also checks if the user is the owner of the paid account
    ],
  })

  // group fetching
  const groups: GroupRecord = useSelector(selectGroups)
  const fetchGroupsStatus = useSelector(selectFetchGroupsStatus)
  const fetchGroupsError = useSelector(selectFetchGroupsError)

  const numGroups = groups ? Object.keys(groups).length : 0

  // permission creation
  const createPermissionStatus = useSelector(selectCreatePermissionStatus)
  const createPermissionError = useSelector(selectCreatePermissionError)
  const createPermissionMessage = useSelector(selectCreatePermissionMessage)

  const [formData, setFormData] = useState<FormState>({
    groupId: undefined,
    role: PermissionRoles.VIEWER,
  })
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  useEffect(() => {
    dispatch(fetchGroups())
    refreshAbilities()
  }, [])

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault()

    // local validations
    const validationErrors = validate()
    setValidationErrors(validationErrors)
    const anyErrors = Object.values(validationErrors).length > 0
    if (anyErrors) {
      return
    } else {
      const { groupId, role } = formData
      const data = {
        permissionable_id: resourceId,
        permissionable_type: resourceType,
        permissable_id: groupId,
        permissable_type: 'Group',
        role,
      }
      dispatch(createPermission(data))
    }
  }

  const handleChange = (e: ChangeObject) => {
    const { name, value } = e
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const validate = () => {
    const errors: ValidationErrors = {}
    if (!formData.groupId) {
      errors.groupId = ['Select a group']
    }
    return errors
  }

  const getGroupOptions = () => {
    if (numGroups === 0) {
      return []
    }
    let options = map(groups, (group: Group) => {
      return {
        text: group.attributes.name,
        value: group.id,
      }
    })

    // add a default value
    options.unshift({
      text: 'Select a group',
      value: '',
    })

    return options
  }

  const getPermissionFormContent = () => {
    const groupSelectOptions = getGroupOptions()
    const roleSelectOptions = getRoleSelectOptions()
    const submitDisabled = createPermissionStatus === Status.LOADING
    return (
      <Form onSubmit={handleFormSubmit}>
        <FormGroup validationErrors={validationErrors?.groupId} className="form-group">
          <Label key="group-select-label" className="label" htmlFor="group-select">
            Choose group
          </Label>
          <Select
            id="group-select"
            key="group-select"
            name="groupId"
            className="select"
            options={groupSelectOptions}
            value={formData.groupId || ''}
            onChange={handleChange}
          />
        </FormGroup>

        <FormGroup validationErrors={validationErrors?.role} className="form-group">
          <Label className="label" htmlFor="role">
            Choose access level
          </Label>
          <Select
            id="role"
            name="role"
            className="select"
            options={roleSelectOptions}
            value={formData.role}
            onChange={handleChange}
          />
        </FormGroup>

        {createPermissionStatus === Status.SUCCEEDED && createPermissionMessage && (
          <div className="notice notice--mini notice--success p">{createPermissionMessage}</div>
        )}

        {createPermissionStatus === Status.FAILED && createPermissionError && (
          <BaseErrorMessages error={createPermissionError} />
        )}

        <FormGroup className="form-group">
          <SpinnerButton className="btn btn--primary" type="submit" name="submit" disabled={submitDisabled}>
            Grant access
          </SpinnerButton>
        </FormGroup>
      </Form>
    )
  }

  if (fetchGroupsStatus === Status.LOADING || fetchGroupsStatus === Status.IDLE) {
    return (
      <div className="p">
        <HorizontalSpinner size="large" />
      </div>
    )
  }

  if (fetchGroupsStatus === Status.FAILED && fetchGroupsError) {
    return (
      <div className="p">
        <BaseErrorMessages error={fetchGroupsError} />
      </div>
    )
  }

  if (fetchGroupsStatus === Status.SUCCEEDED && abilitiesStatus === Status.SUCCEEDED) {
    const groupsFeatureAccess = can('groups_feature_ability')
    const canCreateGroups = can('can_create_groups')

    console.log('Abilities', { groupsFeatureAccess, canCreateGroups })

    const mainContent =
      numGroups > 0 ? (
        getPermissionFormContent()
      ) : (
        <p>You&apos;re not a member of any groups. Access can only be granted to groups you belong to.</p>
      )

    let secondaryContent = null
    if (!groupsFeatureAccess) {
      secondaryContent = (
        <UpgradePrompt
          title="If you are on a premium plan, you can create groups."
          ctaText="Upgrade now"
          ctaPath="/plans"
        />
      )
    } else if (canCreateGroups) {
      secondaryContent = (
        <p>
          You can create a group <a href="/groups">here</a>.
        </p>
      )
    } else {
      secondaryContent = <p>You can ask the owner of the account to create a group for you.</p>
    }
    return (
      <div className="p">
        <div className="block">{mainContent}</div>
        <div className="block">{secondaryContent}</div>
      </div>
    )
  }
}

export default GroupPermissionCreator
